import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BadgeCheck, Shield, Users, Crown, Check } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const badges = [
  { icon: BadgeCheck, label: 'Verified' },
  { icon: Users, label: 'Reference-checked' },
  { icon: Shield, label: 'Security-cleared' },
  { icon: Crown, label: 'Board experience' },
];

const checklist = [
  'Identity verification',
  'CV + LinkedIn review',
  'Reference calls',
  'Credential checks',
  'Background check (optional)',
  'Admin approval',
];

export default function TrustSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<(HTMLDivElement | null)[]>([]);
  const checklistRef = useRef<HTMLDivElement>(null);
  const checkRefs = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // Phase 1 (0-30%): Entrance
      scrollTl.fromTo(
        headlineRef.current,
        { y: 18, opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0
      );

      badgesRef.current.forEach((badge, index) => {
        if (badge) {
          scrollTl.fromTo(
            badge,
            { scale: 0.92, opacity: 0 },
            { scale: 1, opacity: 1, ease: 'none' },
            0.12 + index * 0.03
          );
        }
      });

      scrollTl.fromTo(
        checklistRef.current,
        { y: '10vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.16
      );

      checkRefs.current.forEach((check, index) => {
        if (check) {
          scrollTl.fromTo(
            check,
            { scale: 0 },
            { scale: 1, ease: 'back.out(1.5)' },
            0.22 + index * 0.015
          );
        }
      });

      // Phase 2 (30-70%): Settle

      // Phase 3 (70-100%): Exit
      scrollTl.fromTo(
        headlineRef.current,
        { opacity: 1 },
        { opacity: 0, ease: 'power2.in' },
        0.7
      );

      badgesRef.current.forEach((badge) => {
        if (badge) {
          scrollTl.fromTo(
            badge,
            { opacity: 1 },
            { opacity: 0, ease: 'power2.in' },
            0.72
          );
        }
      });

      scrollTl.fromTo(
        checklistRef.current,
        { y: 0, opacity: 1 },
        { y: '10vh', opacity: 0, ease: 'power2.in' },
        0.7
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="trust"
      className="section-pinned bg-navy flex items-center z-[90]"
    >
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Headline */}
          <div ref={headlineRef} className="mb-12 opacity-0">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
              Trust & Safety
            </span>
            <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white mb-6">
              Verified profiles. Transparent trust.
            </h2>
            <p className="text-body-lg text-slate max-w-2xl mx-auto">
              Identity, credentials, references, and background checks—admin-reviewed
              before badges are awarded.
            </p>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {badges.map((badge, index) => (
              <div
                key={badge.label}
                ref={(el) => { badgesRef.current[index] = el; }}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full opacity-0"
              >
                <badge.icon className="w-4 h-4 text-teal" />
                <span className="text-sm font-medium text-white">{badge.label}</span>
              </div>
            ))}
          </div>

          {/* Checklist Card */}
          <div
            ref={checklistRef}
            className="surface-panel p-8 max-w-2xl mx-auto opacity-0"
          >
            <h3 className="font-heading text-lg font-semibold text-white mb-6">
              Verification Process
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {checklist.map((item, index) => (
                <div key={item} className="flex items-center gap-3">
                  <div
                    ref={(el) => { checkRefs.current[index] = el; }}
                    className="w-5 h-5 rounded-full bg-teal/20 flex items-center justify-center flex-shrink-0"
                    style={{ transform: 'scale(0)' }}
                  >
                    <Check className="w-3 h-3 text-teal" />
                  </div>
                  <span className="text-sm text-slate text-left">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
